import { DesignTokens } from './types';
// See https://www.figma.com/file/HmuwffBW30v1RfCEo81hEU/Design-system?node-id=4039%3A8516

/*
 * Using the flex-direction property with values of row-reverse or column-reverse will create a disconnect between
 * the visual presentation of content and DOM order. So we omit these are options that can be used for FlexDirection.
 */
export enum FlexDirection {
  Row = 'row',
  Column = 'column',
}

export enum FlexWrap {
  Nowrap = 'nowrap',
  Wrap = 'wrap',
}

export enum JustifyContent {
  Center = 'center',
  SpaceBetween = 'space-between',
  SpaceAround = 'space-around',
  SpaceEvenly = 'space-evenly',
  FlexStart = 'flex-start',
  FlexEnd = 'flex-end',
}

export enum AlignItems {
  Center = 'center',
  FlexStart = 'flex-start',
  FlexEnd = 'flex-end',
  Stretch = 'stretch',
  Baseline = 'baseline',
}

export enum AlignSelf {
  Center = 'center',
  End = 'end',
  Start = 'start',
  Stretch = 'stretch',
}

export enum PlaceSelf {
  Center = 'center',
  FlexStart = 'flex-start',
  FlexEnd = 'flex-end',
  SelfStart = 'self-start',
  SelfEnd = 'self-end',
}

export enum Display {
  None = 'none',
  Block = 'block',
  InlineBlock = 'inline-block',
  Flex = 'flex',
  InlineFlex = 'inline-flex',
  Inline = 'inline',
  Inherit = 'inherit',
  Initial = 'initial',
}

export enum GridDisplay {
  Grid = 'grid',
  InlineGrid = 'inline-grid',
}

export enum GridPlacement {
  Start = 'start',
  End = 'end',
  Center = 'center',
  Stretch = 'stretch',
}

export const baseSpaceScale = 4;
export type SpaceScale =
  | 0
  | 0.25
  | 0.5
  | 0.75
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 8
  | 10
  | 12
  | 14
  | 16
  | 18
  | 20;

export const baseRadiusScale = 2;
export type RadiusScale = 0.5 | 1 | 1.5 | 'round';

export const baseBorderScale = 2;
export type BorderScale = 0 | 0.5 | 1 | 1.5 | 2;

export enum Overflow {
  Scroll = 'scroll',
  Hidden = 'hidden',
  Visible = 'visible',
}

export enum DisplayPosition {
  Relative = 'relative',
  Absolute = 'absolute',
  Fixed = 'fixed',
  Unset = 'unset',
}

export enum FontFamily {
  UI = 'ui',
  Bold = 'bold',
  SemiBold = 'semi-bold',
  Mono = 'mono',
}

type ValueOf<T> = T[keyof T];

export type FontFamilies = ValueOf<typeof FontFamily>;

// TODO: fix this so these numbers are driven from the tokens
export enum FontWeight {
  Bold = 700,
  SemiBold = 600,
  Normal = 300,
}

export enum TextAlign {
  Left = 'left',
  Center = 'center',
  Right = 'right',
}

export enum LetterSpacing {
  Normal = 'normal',
  Tight = '-0.005em',
}

export enum BorderStyle {
  Solid = 'solid',
  Dashed = 'dashed',
}

export enum Breakpoint {
  Small = '640px',
  Medium = '960px',
  Large = '1200px',
}

export enum ZIndex {
  Lowest = 'Lowest',
  Auto = 'Auto',
  Default = 'Default',
  Masked = 'Masked',
  Mask = 'Mask',
  Sticky = 'Sticky',
  Header = 'Header',
  Toast = 'Toast',
  Dropdown = 'Dropdown',
  Overlay = 'Overlay',
  Spinner = 'Spinner',
  Modal = 'Modal',
  Popup = 'Popup',
  Top = 'Top',
}

export const zIndices: Record<ZIndex, number | 'auto'> = {
  [ZIndex.Lowest]: -999999,
  [ZIndex.Auto]: 'auto',
  [ZIndex.Default]: 1,
  [ZIndex.Masked]: 100,
  [ZIndex.Mask]: 200,
  [ZIndex.Sticky]: 300,
  [ZIndex.Header]: 400,
  [ZIndex.Toast]: 500,
  [ZIndex.Dropdown]: 600,
  [ZIndex.Overlay]: 700,
  [ZIndex.Spinner]: 800,
  [ZIndex.Modal]: 900,
  [ZIndex.Popup]: 1000,
  [ZIndex.Top]: 999999,
};

export enum Color {
  // Primary Colors
  Primary_50 = 'Primary_50', // Duplicated in errors
  Primary_100 = 'Primary_100',
  Primary_200 = 'Primary_200',
  Primary_300 = 'Primary_300',
  Primary_400 = 'Primary_400',
  Primary_500 = 'Primary_500',
  Primary_600 = 'Primary_600',
  Primary_700 = 'Primary_700',
  Primary_800 = 'Primary_800',
  Primary_900 = 'Primary_900',
  // Secondary Colors
  Secondary_50 = 'Secondary_50',
  Secondary_100 = 'Secondary_100',
  Secondary_200 = 'Secondary_200',
  Secondary_300 = 'Secondary_300',
  Secondary_400 = 'Secondary_400',
  Secondary_500 = 'Secondary_500',
  Secondary_600 = 'Secondary_600',
  Secondary_700 = 'Secondary_700',
  Secondary_800 = 'Secondary_800',
  Secondary_900 = 'Secondary_900',
  // Cold Grey Colors
  ColdGrey_50 = 'ColdGrey_50',
  ColdGrey_100 = 'ColdGrey_100',
  ColdGrey_200 = 'ColdGrey_200',
  ColdGrey_300 = 'ColdGrey_300',
  ColdGrey_400 = 'ColdGrey_400',
  ColdGrey_500 = 'ColdGrey_500',
  ColdGrey_600 = 'ColdGrey_600',
  ColdGrey_700 = 'ColdGrey_700',
  ColdGrey_800 = 'ColdGrey_800',
  ColdGrey_900 = 'ColdGrey_900',
  // Neutral Grey Colors
  NeutralGrey_50 = 'NeutralGrey_50',
  NeutralGrey_100 = 'NeutralGrey_100',
  NeutralGrey_200 = 'NeutralGrey_200',
  NeutralGrey_300 = 'NeutralGrey_300',
  NeutralGrey_400 = 'NeutralGrey_400',
  NeutralGrey_500 = 'NeutralGrey_500',
  NeutralGrey_600 = 'NeutralGrey_600',
  NeutralGrey_700 = 'NeutralGrey_700',
  NeutralGrey_800 = 'NeutralGrey_800',
  NeutralGrey_900 = 'NeutralGrey_900',
  // Error Colors
  Error_50 = 'Error_50',
  Error_100 = 'Error_100',
  Error_200 = 'Error_200',
  Error_300 = 'Error_300',
  Error_400 = 'Error_400',
  Error_500 = 'Error_500',
  Error_600 = 'Error_600',
  Error_700 = 'Error_700',
  Error_800 = 'Error_800',
  Error_900 = 'Error_900',
  // Success Colors
  Success_50 = 'Success_50',
  Success_100 = 'Success_100',
  Success_200 = 'Success_200',
  Success_300 = 'Success_300',
  Success_400 = 'Success_400',
  Success_500 = 'Success_500',
  Success_600 = 'Success_600',
  Success_700 = 'Success_700',
  Success_800 = 'Success_800',
  Success_900 = 'Success_900',
  // Warning Colors
  Warning_50 = 'Warning_50',
  Warning_100 = 'Warning_100',
  Warning_200 = 'Warning_200',
  Warning_300 = 'Warning_300',
  Warning_400 = 'Warning_400',
  Warning_500 = 'Warning_500',
  Warning_600 = 'Warning_600',
  Warning_700 = 'Warning_700',
  Warning_800 = 'Warning_800',
  Warning_900 = 'Warning_900',
  // Informative Color
  Informative_50 = 'Informative_50',
  Informative_100 = 'Informative_100',
  Informative_200 = 'Informative_200',
  Informative_300 = 'Informative_300',
  Informative_400 = 'Informative_400',
  Informative_500 = 'Informative_500',
  Informative_600 = 'Informative_600',
  Informative_700 = 'Informative_700',
  Informative_800 = 'Informative_800',
  Informative_900 = 'Informative_900',
  White = 'White',
  Transparent = 'Transparent',
  Inherit = 'Inherit',
}

export const colors: Record<Color, string> = {
  // Primary Colors
  [Color.Primary_50]: '#FFEBEF', // Duplicated in errors
  [Color.Primary_100]: '#FFCCD4',
  [Color.Primary_200]: '#FE969C',
  [Color.Primary_300]: '#F96B74',
  [Color.Primary_400]: '#FF3F50',
  [Color.Primary_500]: '#FF1A32',
  [Color.Primary_600]: '#FF0032',
  [Color.Primary_700]: '#ED002D',
  [Color.Primary_800]: '#E00025',
  [Color.Primary_900]: '#D30016',
  // Secondary Colors
  [Color.Secondary_50]: '#EAE9FF',
  [Color.Secondary_100]: '#CBC7FF',
  [Color.Secondary_200]: '#A29EFF',
  [Color.Secondary_300]: '#7D86FE',
  [Color.Secondary_400]: '#615CFF',
  [Color.Secondary_500]: '#4538FA',
  [Color.Secondary_600]: '#412DEE',
  [Color.Secondary_700]: '#381DE1',
  [Color.Secondary_800]: '#3001D6',
  [Color.Secondary_900]: '#2A00BD',
  // Cold Grey Colors
  [Color.ColdGrey_50]: '#ECEDEF',
  [Color.ColdGrey_100]: '#CED4D9',
  [Color.ColdGrey_200]: '#AEB7C0',
  [Color.ColdGrey_300]: '#8D9AA7',
  [Color.ColdGrey_400]: '#768594',
  [Color.ColdGrey_500]: '#5E7183',
  [Color.ColdGrey_600]: '#516272',
  [Color.ColdGrey_700]: '#424F5C',
  [Color.ColdGrey_800]: '#333D47',
  [Color.ColdGrey_900]: '#212930',
  // Neutral Grey Colors
  [Color.NeutralGrey_50]: '#F4F7FA',
  [Color.NeutralGrey_100]: '#DCDFE1',
  [Color.NeutralGrey_200]: '#C4C7C9',
  [Color.NeutralGrey_300]: '#ADAEB0',
  [Color.NeutralGrey_400]: '#959698',
  [Color.NeutralGrey_500]: '#7D7E7F',
  [Color.NeutralGrey_600]: '#656667',
  [Color.NeutralGrey_700]: '#4E4D4E',
  [Color.NeutralGrey_800]: '#363536',
  [Color.NeutralGrey_900]: '#1E1D1D',
  // Error Colors
  [Color.Error_50]: '#FFEBEF',
  [Color.Error_100]: '#FFCED5',
  [Color.Error_200]: '#F09BA0',
  [Color.Error_300]: '#E7737A',
  [Color.Error_400]: '#F3515A',
  [Color.Error_500]: '#F93E41',
  [Color.Error_600]: '#EA3540',
  [Color.Error_700]: '#D82B39',
  [Color.Error_800]: '#CB2432',
  [Color.Error_900]: '#BC1526',
  // Success Colors
  [Color.Success_50]: '#E6F7E7',
  [Color.Success_100]: '#C3EAC4',
  [Color.Success_200]: '#9CDC9E',
  [Color.Success_300]: '#70CF76',
  [Color.Success_400]: '#4BC456',
  [Color.Success_500]: '#17B834',
  [Color.Success_600]: '#01A92B',
  [Color.Success_700]: '#00971E',
  [Color.Success_800]: '#008610',
  [Color.Success_900]: '#006700',
  // Warning Colors
  [Color.Warning_50]: '#FAEAE7',
  [Color.Warning_100]: '#FDCEBD',
  [Color.Warning_200]: '#FDAF93',
  [Color.Warning_300]: '#FC9068',
  [Color.Warning_400]: '#FB7846',
  [Color.Warning_500]: '#FB6126',
  [Color.Warning_600]: '#F05C22',
  [Color.Warning_700]: '#E2551D',
  [Color.Warning_800]: '#D44E1A',
  [Color.Warning_900]: '#BB4213',
  // Informative Color
  [Color.Informative_50]: '#DFF4FF',
  [Color.Informative_100]: '#ADE3FD',
  [Color.Informative_200]: '#73D1FC',
  [Color.Informative_300]: '#26BFFB',
  [Color.Informative_400]: '#00B1FB',
  [Color.Informative_500]: '#00A3F9',
  [Color.Informative_600]: '#0095EA',
  [Color.Informative_700]: '#0082D5',
  [Color.Informative_800]: '#0071C1',
  [Color.Informative_900]: '#00519F',
  // White
  [Color.White]: '#FFFFFF',
  // Transparent
  [Color.Transparent]: 'transparent',
  // Inherit
  [Color.Inherit]: 'inherit',
};

const borderShadowColor = colors[Color.ColdGrey_800];
const blurryShadowColor = colors[Color.ColdGrey_600];
/**
 * Elevation styles are applied as box shadows.
 * Available levels: 0, 1, 2, 3, 4.
 */
export const elevation = [
  `0 0 1px ${borderShadowColor}`,
  `0 0 1px ${borderShadowColor}, 0 2px 4px -2px ${blurryShadowColor}`,
  `0 0 1px ${borderShadowColor}, 0 5px 8px -4px ${blurryShadowColor}`,
  `0 0 1px ${borderShadowColor}, 0 8px 10px -4px ${blurryShadowColor}`,
  `0 0 1px ${borderShadowColor}, 0 16px 24px -8px ${blurryShadowColor}`,
];

export const defaultDesignTokens: DesignTokens = {
  FontFamilies: {
    [FontFamily.UI]: '"AxiformaBook", Helvetica, sans-serif',
    [FontFamily.Bold]: '"AxiformaBold", Helvetica, sans-serif',
    [FontFamily.SemiBold]: '"AxiformaSemiBold", Helvetica, sans-serif',
    [FontFamily.Mono]: '"AxiformaBook", Helvetica, sans-serif',
  },
  defaultFontColor: Color.ColdGrey_800,
  typographyDefaults: {
    h1: {
      size: [8, 9, 9, 10],
      fontFamily: FontFamily.SemiBold,
      letterSpacing: [LetterSpacing.Tight],
    },
    h2: {
      size: [7, 8, 8, 9],
      fontFamily: FontFamily.SemiBold,
      letterSpacing: [LetterSpacing.Normal, LetterSpacing.Tight],
    },
    h3: {
      size: [6, 7, 7, 8],
      fontFamily: FontFamily.SemiBold,
      letterSpacing: [LetterSpacing.Normal, null, null, LetterSpacing.Tight],
    },
    h4: {
      size: [5, 6, 6, 7],
      fontFamily: FontFamily.SemiBold,
      letterSpacing: [LetterSpacing.Normal],
    },
    h5: {
      size: [4, 5, 5, 5],
      fontFamily: FontFamily.SemiBold,
      letterSpacing: [LetterSpacing.Normal],
    },
    h6: {
      size: [3, 4, 4, 4],
      fontFamily: FontFamily.SemiBold,
      letterSpacing: [LetterSpacing.Normal],
    },
  },
  fontSizes: [
    {
      fontSize: '12px',
      lineHeight: '20px',
    },
    {
      fontSize: '13px',
      lineHeight: '20px',
    },
    {
      fontSize: '15px',
      lineHeight: '24px',
    },
    {
      fontSize: '18px',
      lineHeight: '24px',
    },
    {
      fontSize: '20px',
      lineHeight: '24px',
    },
    {
      fontSize: '24px',
      lineHeight: '32px',
    },
    {
      fontSize: '28px',
      lineHeight: '40px',
    },
    {
      fontSize: '32px',
      lineHeight: '40px',
    },
    {
      fontSize: '40px',
      lineHeight: '48px',
    },
    {
      fontSize: '48px',
      lineHeight: '56px',
    },
    {
      fontSize: '64px',
      lineHeight: '80px',
    },
  ],
  defaultFontSize: 2,
  colors,
  spacingScale: baseSpaceScale,
  breakpoints: [Breakpoint.Small, Breakpoint.Medium, Breakpoint.Large],
  radiusScale: baseRadiusScale,
  borderScale: baseBorderScale,
  borderStyle: {
    [BorderStyle.Solid]: BorderStyle.Solid,
    [BorderStyle.Dashed]: BorderStyle.Dashed,
  },
  zIndices,
};
